import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { combineLatest, forkJoin, interval, of, Subscription, throwError, timer } from 'rxjs'
import { catchError, finalize, first, shareReplay, switchMap, tap } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { RestoreTriggerDto, SyncPhaseDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { ApplicationResource } from 'src/app/shared/resource-viewer-base'
import { shortTimeString, timeAgoString } from 'src/app/shared/utils'
import { handleArpioError } from 'src/utils/handle-error'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from 'src/app/account/services/account.service'
import { Application, ApplicationProviderService } from 'src/app/account/services/application.service'
import { ConcludeTestSettings } from 'src/app/account/components/conclude-test-dialog/conclude-test-dialog.component'
import { FailbackSettings } from 'src/app/account/components/failback-dialog/failback-dialog.component'
import {
  computeProcessStatus,
  ProcessingStatus,
} from 'src/app/account/components/process-state/process-state.component'
import { RelevantStatus } from 'src/app/shared/types'
import { computeStatusText } from 'src/app/account/components/application/statusText'
import { Http409Error } from 'src/utils/errors'
import { LaunchRestoreSettings } from 'src/app/account/components/launch-sync-dialog-base'
import { isStatusQuarantineType } from 'src/app/account/components/app-resources-view/app-resources-view.component'
import { LoggerService } from '../../services/logger.service'
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog'
import { AwsEventLogsComponent } from '../aws-event-logs/aws-event-logs.component'
import { AccessLevelService, AppPermission } from 'src/app/services/access-level.service'

export enum ResourcesTab {
  RECOVERY,
  FAILBACK,
}

@Component({
  selector: 'app-application',
  templateUrl: './application.component.html',
  styleUrls: ['./application.component.scss'],
  providers: [DialogService],
  standalone: false,
})
export class ApplicationComponent implements OnInit, OnDestroy {
  static APPLICATION_REFRESH_INTERVAL = 60000
  static RAPID_REFRESH_INTERVAL = 2000

  pendingRecoverySettings: LaunchRestoreSettings[] | null = null
  showAreYouSure: boolean = false
  application: Application = null
  selectedApplications: Application[] = []
  quarantineEnabled: boolean = false

  currentTab = ResourcesTab.RECOVERY
  failbackStarted: Date = null

  filterText = ''

  @ViewChild('failoverDialog', { static: true })
  failoverDialog: ModalDialogComponent
  @ViewChild('concludeFailoverDialog', { static: true })
  concludeFailoverDialog: ModalDialogComponent
  @ViewChild('testFailoverDialog', { static: true })
  testFailoverDialog: ModalDialogComponent
  @ViewChild('sandboxConfigDialog', { static: true })
  sandboxConfigDialog: ModalDialogComponent
  @ViewChild('concludeTestFailoverDialog', { static: true })
  concludeTestFailoverDialog: ModalDialogComponent
  @ViewChild('failbackDialog', { static: true })
  failbackDialog: ModalDialogComponent
  @ViewChild('unquarantineDialog', { static: true })
  unquarantineDialog: ModalDialogComponent
  @ViewChild('awsEventLogsDialog', { static: true })
  awsEventLogsDialog: ModalDialogComponent

  readonly Tab = ResourcesTab
  private applicationIdSubscription: Subscription = null
  private applicationSubscription: Subscription = null
  private forceRefreshSubscription: Subscription = null
  private launchSyncSubscription: Subscription = null
  private launchFailbackSubscription: Subscription = null
  private applicationRefreshTimerSubscription: Subscription = null
  private timerIntegrityCheckSubscription: Subscription = null

  private rapidRefreshExpires: Date = null
  // This boolean allows us to handle the case where we just changed the ApplicationDto, but
  // due to the replication lag DynamoDB Global Tables, an immediate refresh
  // will fetch stale data. This boolean causes us to skip setting the ApplicationDto for the
  // first refresh. Subsequent refreshes (i.e. after 5s) will do a normal refresh.
  private delayRapidRefresh: boolean = false
  // This boolean forces a refresh of resources when navigating from the editor
  private forceRefreshResources: boolean = false
  // Track when a background poll is actively fetching data (for spinner visualization)
  isPolling: boolean = false
  // Track when the user manually clicked refresh (for button spinner only)
  manualRefreshing: boolean = false
  applicationId: string
  // Track when resources are being refreshed after navigating from the editor
  resourcesRefreshing: boolean = false
  private resourcesRefreshTimeoutSubscription: Subscription = null

  sandboxEventsDialogRef: DynamicDialogRef | undefined

  // Permission states
  private canTestPermission = false
  private canRecoverPermission = false
  private canManagePermission = false
  private permissionsSubscription: Subscription = null

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private applicationProviderService: ApplicationProviderService,
    private apiService: ApiService,
    private uiService: UiService,
    private ref: ChangeDetectorRef,
    private logger: LoggerService,
    private dialogService: DialogService,
    private accessLevelService: AccessLevelService
  ) {}

  ngOnInit() {
    // Initialize permissions
    this.initializePermissions()

    // When the account ID or application ID change, re-initialize the application.
    // We don't read the account ID from the URL because we want to wait for the DTO
    // to load.  We need it throughout for the multi-account flag.  Instead, we listen
    // to the accountIdStream on the account service which emits account IDs when they change.
    this.applicationIdSubscription = combineLatest([this.accountService.accountIdStream, this.route.paramMap])
      .pipe(finalize(() => (this.applicationIdSubscription = null)))
      .subscribe(([accountId, paramMap]) => {
        this.applicationId = paramMap.get('applicationId')
        // Update permissions for this specific application
        this.updatePermissionsForApplication(this.applicationId)
        this.initializeApplication(accountId, paramMap.get('applicationId'))
      })
  }

  ngOnDestroy() {
    if (subscriptionActive(this.applicationIdSubscription)) {
      this.applicationIdSubscription.unsubscribe()
      this.applicationIdSubscription = null
    }

    if (subscriptionActive(this.permissionsSubscription)) {
      this.permissionsSubscription.unsubscribe()
      this.permissionsSubscription = null
    }

    this.cleanup()
  }

  private initializePermissions() {
    // We need to wait for the application ID to be available
    // The actual permission check will be done when initializing the application
    this.canTestPermission = false
    this.canRecoverPermission = false
    this.canManagePermission = false
  }

  private updatePermissionsForApplication(applicationId: string) {
    if (this.permissionsSubscription) {
      this.permissionsSubscription.unsubscribe()
    }

    // Check permissions directly using AppPermission enums
    this.permissionsSubscription = combineLatest([
      this.accessLevelService.hasAppPermission(applicationId, AppPermission.MANAGE_TEST),
      this.accessLevelService.hasAppPermission(applicationId, AppPermission.MANAGE_RECOVERY),
      this.accessLevelService.hasAppPermission(applicationId, AppPermission.UPDATE_APP),
    ]).subscribe(([canTest, canRecover, canManage]) => {
      this.canTestPermission = canTest
      this.canRecoverPermission = canRecover
      this.canManagePermission = canManage
      this.ref.markForCheck()
    })
  }

  get recoveryPointAttempts() {
    return this.application.recoveryPointAttempts || []
  }

  private cancelApplicationSubscription() {
    if (subscriptionActive(this.applicationSubscription)) {
      this.applicationSubscription.unsubscribe()
    }
    this.applicationSubscription = null
  }

  private cancelApplicationRefreshTimerSubscription() {
    if (this.applicationRefreshTimerSubscription) {
      this.applicationRefreshTimerSubscription.unsubscribe()
    }
    this.applicationRefreshTimerSubscription = null
  }

  private cancelSubscriptionsInCorrectOrder() {
    // This helper function will cancel the application subscription and the refresh timer.  These
    // need to happen in a specific order, so we have this 1 function to get it right.

    // In most places where we create a new application subscription, we set up a finalize() block
    // that will reschedule the refresh timer once the application subscription completes.
    // If we want both subscriptions canceled, but we cancel the application subscription after
    // we cancel the refresh subscription, the finalize block will just reschedule the refresh
    // subscription that we just canceled.

    // Always cancel the application subscription first.

    this.cancelApplicationSubscription()
    this.cancelApplicationRefreshTimerSubscription()
  }

  private cleanup() {
    this.cancelSubscriptionsInCorrectOrder()

    if (subscriptionActive(this.timerIntegrityCheckSubscription)) {
      this.timerIntegrityCheckSubscription.unsubscribe()
      this.timerIntegrityCheckSubscription = null
    }

    if (subscriptionActive(this.forceRefreshSubscription)) {
      this.forceRefreshSubscription.unsubscribe()
      this.forceRefreshSubscription = null
    }

    if (subscriptionActive(this.launchSyncSubscription)) {
      this.launchSyncSubscription.unsubscribe()
      this.launchSyncSubscription = null
    }

    if (subscriptionActive(this.resourcesRefreshTimeoutSubscription)) {
      this.resourcesRefreshTimeoutSubscription.unsubscribe()
      this.resourcesRefreshTimeoutSubscription = null
    }
    this.resourcesRefreshing = false

    this.application = null
  }

  get loadingApplication() {
    return !this.application
  }

  public setApplication(application: Application) {
    this.application = application
  }

  private get applicationRefreshInterval() {
    if (!!this.application && this.application.currentlyActive) {
      return ApplicationComponent.RAPID_REFRESH_INTERVAL
    }
    // Use rapid refresh when application is marked as deleted but not fully deleted yet
    if (!!this.application && this.appIsDeleted && !this.hasCompletedDeletionProcess(this.application)) {
      return ApplicationComponent.RAPID_REFRESH_INTERVAL
    }
    if (new Date() < this.rapidRefreshExpires) {
      return ApplicationComponent.RAPID_REFRESH_INTERVAL
    }
    return ApplicationComponent.APPLICATION_REFRESH_INTERVAL
  }

  private initializeApplication(accountId: string, applicationId: string): void {
    /**
     * Load the application object.  This is called every time the URL changes to a new
     * application.
     */

    // shut-down any in-progress data loads, refresh timers, etc.
    this.cleanup()

    // This is a hack to avoid the situation where the timers have gotten lost.  We hope to never need this, but...
    this.timerIntegrityCheckSubscription = interval(10000).subscribe((_) => {
      if (this.nothingActive || this.allActive) {
        console.error('The timers have been lost.  Forcing a reload.')
        window.location.reload()
      }
    })

    this.setApplication(null)
    this.currentTab = ResourcesTab.RECOVERY

    // If we're navigating here from another page (like the application editor page) that may have kicked off
    // some activity on this application, we might want to refresh immediately.  But there's a race condition
    // where an immediate refresh might not pick up activity that was kicked off on the prior page.
    // This rapidRefresh setting indicates that we should refresh frequently for the first bit of time so
    // that we will pick up that activity.  It's turned on via window history from the prior page.
    if (!!window.history.state) {
      this.rapidRefreshExpires = window.history.state.rapidRefreshExpires || null
      this.delayRapidRefresh = !!window.history.state.delayRapidRefresh
      this.forceRefreshResources = !!window.history.state.forceRefreshResources
    }

    // When navigating from the editor with forceRefreshResources, show a loading spinner
    // in the resources table until fresh data arrives
    if (this.forceRefreshResources) {
      this.resourcesRefreshing = true
      this.resourcesRefreshTimeoutSubscription = timer(5000).subscribe(() => {
        this.resourcesRefreshing = false
        this.resourcesRefreshTimeoutSubscription = null
        this.ref.markForCheck()
      })
    }

    // Track emissions to know when we've received fresh data after the initial cached emission
    let emissionCount = 0

    this.applicationSubscription = this.applicationProviderService
      .loadApplication$(applicationId, accountId, !this.delayRapidRefresh, this.forceRefreshResources)
      .pipe(
        switchMap((application) => {
          // Check if deleted and resources is empty or falsy to prevent unnecessary api calls
          if (application?.dto?.deleted && (!application.resources || application.resources.length === 0)) {
            // Use ApiService to get the list of applications and check if the current application is in that list
            return this.apiService.listApplications(accountId).pipe(
              first(),
              switchMap((appDtos) => {
                const exists = appDtos.some((appDto: any) => appDto.appId === application.applicationId)
                if (!exists) {
                  this.router.navigate([`/${accountId}/deleted`])
                  // Complete the observable chain
                  return of(null)
                }
                // If exists, continue with the application
                return of(application)
              }),
              catchError((_e) => {
                this.router.navigate([`/${accountId}/deleted`])
                return of(null)
              })
            )
          }
          return of(application)
        }),
        tap((application) => this.pulseSpinnerIfActive(application)),
        finalize(() => {
          this.applicationSubscription = null
          this.isPolling = false
        })
      )
      .subscribe(
        (application) => {
          emissionCount++
          if (application) {
            this.setApplication(application)

            // When forceRefreshResources is true, keep the resources spinner until we've
            // received emissions that should contain fresh resource data.
            // We clear on emission 2 and rely on subsequent emissions to update if needed.
            if (this.resourcesRefreshing && emissionCount >= 2) {
              this.clearResourcesRefreshSpinner()
            }
          }
        },
        handleArpioError(window.location.href, this.apiService, this.uiService),
        () => this.rescheduleApplicationRefresh()
      )
  }

  private clearResourcesRefreshSpinner(): void {
    if (subscriptionActive(this.resourcesRefreshTimeoutSubscription)) {
      this.resourcesRefreshTimeoutSubscription.unsubscribe()
      this.resourcesRefreshTimeoutSubscription = null
    }
    this.resourcesRefreshing = false
    this.ref.markForCheck()
  }

  private refreshApplication(): void {
    this.cancelApplicationSubscription()

    // Show spinner during active operations
    if (this.application?.currentlyActive) {
      this.isPolling = true
    }

    this.applicationSubscription = this.application
      .refresh$(true)
      .pipe(
        tap((application) => this.pulseSpinnerIfActive(application)),
        finalize(() => {
          this.applicationSubscription = null
          this.isPolling = false
          this.rescheduleApplicationRefresh()
        })
      )
      .subscribe(
        (application) => this.setApplication(application),
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  rescheduleApplicationRefresh() {
    this.applicationRefreshTimerSubscription = timer(this.applicationRefreshInterval).subscribe((_) =>
      this.refreshApplication()
    )
  }

  /**
   * Shows a brief spinner pulse when the application is actively processing.
   * Call this on each poll emission to create a visual indicator of polling activity.
   */
  private pulseSpinnerIfActive(application: Application): void {
    if (application?.currentlyActive) {
      this.isPolling = true
      this.ref.detectChanges()
      // Hide spinner after a short delay to create a "pulse" effect
      setTimeout(() => {
        this.isPolling = false
        this.ref.detectChanges()
      }, 500)
    } else {
      this.isPolling = false
    }
  }

  refreshButtonClick(): void {
    /**
     * The sync pair and application should already be getting refreshed on a schedule,
     * but if the user wants it now, a la Veruca Salt, then we will do it.
     *
     */
    if (!subscriptionActive(this.forceRefreshSubscription)) {
      this.manualRefreshing = true
      this.forceRefreshSubscription = this.application
        .refresh$(true, false, true)
        .pipe(
          finalize(() => {
            this.forceRefreshSubscription = null
            this.manualRefreshing = false
          })
        )
        .subscribe(
          (application) => this.setApplication(application),
          handleArpioError(window.location.href, this.apiService, this.uiService)
        )
    }
  }

  get refreshing() {
    return (
      subscriptionActive(this.forceRefreshSubscription) ||
      (subscriptionActive(this.applicationSubscription) && this.appIsDeleted) ||
      this.isPolling
    )
  }

  getFailbackResources(): Array<ApplicationResource> {
    return this.getResources(RelevantStatus.FAILBACK).filter((res) => res.resourceTypeDetails.supportsFailback)
  }

  getQuarantinedResources(): Array<ApplicationResource> {
    return this.getResources(RelevantStatus.SYNC).filter((res) => isStatusQuarantineType(res.status))
  }

  get issuesUrl() {
    return this.uiService.buildApplicationIssuesUrl(this.accountService.accountId, this.application.applicationId)
  }

  getResources(relevantStatus: RelevantStatus): ApplicationResource[] {
    return this.application.getResources(relevantStatus, this.filterText)
  }

  get lastStagingRequest(): Date {
    return this.application.lastBackupRequest
  }

  get lastStagingStart(): Date {
    return this.application.lastBackupStart
  }

  get lastStagingSuccess(): Date {
    return this.application.lastBackupSuccess
  }

  get lastStagingFailure(): Date {
    return this.application.lastBackupFailure
  }

  get lastSyncRequest(): Date {
    return this.application.lastRestoreRequest
  }

  get lastSyncStart(): Date {
    return this.application.lastRestoreStart
  }

  get lastSyncSuccess(): Date {
    return this.application.lastRestoreSuccess
  }

  get lastSyncFailure(): Date {
    return this.application.lastRestoreFailure
  }

  get targetEnvironmentRecoveryPointTime() {
    const currentRp = this.application.currentRecoveryPoint
    return currentRp ? currentRp.timestamp : null
  }

  public computeApplicationStatus() {
    const stagingStatus = computeProcessStatus(
      this.application.lastBackupRequest,
      this.application.lastBackupStart,
      this.application.lastBackupSuccess,
      this.application.lastBackupFailure
    )

    const syncStatus = computeProcessStatus(
      this.application.lastRestoreRequest,
      this.application.lastRestoreStart,
      this.application.lastRestoreSuccess,
      this.application.lastRestoreFailure
    )

    let backupIssues = this.application.backupIssues || []
    let restoreIssues = this.application.restoreIssues || []
    let scanIssues = this.application.scanIssues || []

    // select the most recent recovery point timestamp based on appGropup status
    let latestRpTimeStamp: Date
    if (this.accountService.entitledForCapability('appGroupRestore')) {
      // latestRpTimeStamp for appGroupRestore is the maximum recoveryPoints date
      if (this.application.recoveryPoints) {
        latestRpTimeStamp = this.application.recoveryPoints.reduce(
          (accum, curr) => (!!accum ? (accum.timestamp > curr.timestamp ? accum : curr) : curr),
          null
        )?.timestamp
      } else {
        latestRpTimeStamp = null
      }
    } else {
      latestRpTimeStamp = this.targetEnvironmentRecoveryPointTime // Stick to default logic
    }

    const [status, text, relevantStatus, issues] = computeStatusText(
      this.application.syncPhase,
      stagingStatus,
      syncStatus,
      latestRpTimeStamp,
      backupIssues,
      restoreIssues,
      scanIssues
    )

    return { class: status.valueOf(), text, relevantStatus, issues }
  }

  public recoverButtonClick() {
    this.failoverDialog.open()
  }

  private setApplicationSyncPhase(restoreSettings: Array<LaunchRestoreSettings>, syncPhase: SyncPhaseDto) {
    /*
      Invoke the server API to launch a sync/restore.
      This method launches the restore, and then returns an observable that will emit once
      when the restore has successfully begun.  We do some rxjs trickery to make one API
      call emit to multiple subscribers here (see the comments below).
    */

    // Cancel the applicaiton subscription and refresh subscription.
    this.cancelSubscriptionsInCorrectOrder()

    // Create an observable that can be shared across multiple subscriptions.  We do this
    // with the shareReplay operator, specifying a buffer size of 1 so that late subscriptions
    // will always get the most recently emitted value if one exists.  We also use the
    // refCount flag so that when all subscribers have stopped listening, the observable
    // will stop emitting.
    const updateObservable = Application.updateApplicationSyncPhases$(
      this.application,
      restoreSettings,
      syncPhase
    ).pipe(shareReplay({ bufferSize: 1, refCount: true }))

    // Now, subscribe to that observable for the first time.  This is the subscription that will
    // poll until the restore has completed.
    this.applicationSubscription = updateObservable
      .pipe(
        switchMap((_) => this.application.refresh$(false)), // refreshDto=false on first poll: preserve the fresh DTO from initiateRestore to avoid a race where getApplication() returns stale data before the DB write completes
        tap((application) => this.pulseSpinnerIfActive(application)),
        catchError((error) => (error instanceof Http409Error ? this.application.refresh$(true) : throwError(error))),
        finalize(() => {
          this.rescheduleApplicationRefresh()
          this.applicationSubscription = null
          this.isPolling = false
        })
      )
      .subscribe(
        (application) => this.setApplication(application),
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )

    // Finally, return an observable that will emit once when the update API call completes.
    // The caller can subscribe to this and track the subscription state (with subscriptionActive())
    // to know when the call was successful.
    return updateObservable.pipe(first())
  }

  onLaunchRecovery(settings: LaunchRestoreSettings[]) {
    this.selectedApplications = settings.map((s) => s.application)
    this.quarantineEnabled = settings[0]?.quarantineEnabled
    this.pendingRecoverySettings = settings
    this.showAreYouSure = true
  }

  onCancelRecoveryConfirmation() {
    this.showAreYouSure = false
    this.failoverDialog.close()
  }

  confirmLaunchRecovery() {
    if (this.pendingRecoverySettings) {
      this.launchRecovery(this.pendingRecoverySettings)
      this.pendingRecoverySettings = null
      this.showAreYouSure = false
    }
  }

  launchRecovery(restoreSettings: LaunchRestoreSettings[]) {
    /**
     * Places multiple applications into the failover state and then launches
     * a sync on the sync pair.
     */

    this.launchSyncSubscription = this.setApplicationSyncPhase(restoreSettings, SyncPhaseDto.FAILOVER)
      .pipe(finalize(() => (this.launchSyncSubscription = null)))
      .subscribe(
        (_) => {
          this.ref.markForCheck()
          this.failoverDialog.close()
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  initiateFailback(applications: Application[]) {
    /*
      Same kind of logic as setApplicationSyncPhase, but this time for launching a failback.
    */

    // Create an observable that can be shared across multiple subscriptions.  We do this
    // with the shareReplay operator, specifying a buffer size of 1 so that late subscriptions
    // will always get the most recently emitted value if one exists.  We also use the
    // refCount flag so that when all subscribers have stopped listening, the observable
    // will stop emitting.
    const updateObservable = Application.initiateFailback$(this.application, applications).pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    )

    // Now, subscribe to that observable the first time.  This is the subscription that will
    // poll until the failback has completed.
    this.applicationSubscription = updateObservable
      .pipe(
        tap((application) => this.pulseSpinnerIfActive(application)),
        finalize(() => {
          this.rescheduleApplicationRefresh()
          this.applicationSubscription = null
          this.isPolling = false
        })
      )
      .subscribe(
        (application) => this.setApplication(application),
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )

    // Finally, return an observable that will emit once when the update API call completes.
    // The caller can subscribe to this and track the subscription state (with subscriptionActive())
    // to know when the call was successful.
    return updateObservable.pipe(first())
  }

  launchFailback(launchEvent: FailbackSettings) {
    // Cancel any current refresh, or refresh timer.
    this.cancelSubscriptionsInCorrectOrder()

    this.launchFailbackSubscription = this.initiateFailback(launchEvent.applications)
      .pipe(finalize(() => (this.launchFailbackSubscription = null)))
      .subscribe(
        (_) => {
          this.showFailbackTab()
          this.ref.markForCheck()
          this.failbackDialog.close()
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  dismissModal() {
    this.failoverDialog.close()
  }

  concludeRecoveryButtonClick() {
    this.concludeFailoverDialog.open()
  }

  failbackButtonClick() {
    this.failbackDialog.open()
  }

  unquarantineButtonClick() {
    this.unquarantineDialog.open()
  }

  concludeRecovery(concludeEvent: { restoreSettings: Array<LaunchRestoreSettings> }) {
    this.showRecoveryTab() // Failback tab will disappear, so make sure it's not active.

    this.launchSyncSubscription = this.setApplicationSyncPhase(concludeEvent.restoreSettings, SyncPhaseDto.STANDBY)
      .pipe(finalize(() => (this.launchSyncSubscription = null)))
      .subscribe(
        (_) => {
          this.ref.markForCheck()
          this.concludeFailoverDialog.close()
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  dismissConcludeModal() {
    this.concludeFailoverDialog.close()
  }

  launchTestButtonClick() {
    this.logger.info('User clicked failover button - open failover dialog', {
      component: 'OpenModal',
    })
    this.testFailoverDialog.open()
  }

  sandboxConfigButtonClick() {
    this.sandboxConfigDialog.open()
  }

  launchTest(testSettings: LaunchRestoreSettings[]) {
    this.logger.info('User clicked failover button - Test Dialog', {
      component: 'TestButton',
    })
    this.launchSyncSubscription = this.setApplicationSyncPhase(testSettings, SyncPhaseDto.FAILOVER_TEST)
      .pipe(finalize(() => (this.launchSyncSubscription = null)))
      .subscribe(
        (_) => {
          this.ref.markForCheck()
          this.testFailoverDialog.close()
          this.sandboxConfigDialog.close()
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  dismissLaunchTestModal() {
    this.testFailoverDialog.close()
  }

  dismissSandboxConfigModal() {
    this.sandboxConfigDialog.close()
  }

  get launchingSync() {
    return subscriptionActive(this.launchSyncSubscription)
  }

  get launchingFailback() {
    return subscriptionActive(this.launchFailbackSubscription)
  }

  concludeTestButtonClick() {
    this.concludeTestFailoverDialog.open()
  }

  concludeTestFailover(concludeEvent: ConcludeTestSettings) {
    const { restoreSettings, successfulTest, testNotes } = concludeEvent

    // Cancel any current application polling or refresh timers.  We'll re-schedule them when this process completes.
    this.cancelSubscriptionsInCorrectOrder()

    // build an array of result submissions for the selected applications.
    // fork-join with an empty list will never emit.  We need to at least emit a null.
    let applicationUpdates$ = [of<Application>(null)]
    if (restoreSettings.length > 0) {
      applicationUpdates$ = restoreSettings.map((rs) => {
        return rs.application.submitFailoverTestResults$(successfulTest, testNotes).pipe(
          tap((application) => {
            if (application.applicationId === this.application.applicationId) {
              this.setApplication(application)
            }
          })
        )
      })
    }

    // We need to close the dialog after the sync is initiated, and show the spinny on the conclude button
    // between the button click and the dialog close.  To do this, we'll need 2 subscriptions against the
    // same underlying observable.  We'll use the shareReplay operator to build an observable that can
    // be subscribed to twice.

    const updateObservable = forkJoin(applicationUpdates$).pipe(
      switchMap((_applications) => {
        return Application.updateApplicationSyncPhases$(
          this.application,
          concludeEvent.restoreSettings,
          SyncPhaseDto.STANDBY
        )
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    )

    // Now, subscribe to that observable for the first time.  This is the subscription that will
    // poll until the restore has completed.
    this.applicationSubscription = updateObservable
      .pipe(
        switchMap((_) => this.application.refresh$(false)), // refreshDto=false on first poll: preserve the fresh DTO from initiateRestore to avoid a race where getApplication() returns stale data before the DB write completes
        tap((application) => this.pulseSpinnerIfActive(application)),
        catchError((error) => (error instanceof Http409Error ? this.application.refresh$(true) : throwError(error))),
        finalize(() => {
          this.rescheduleApplicationRefresh()
          this.applicationSubscription = null
          this.isPolling = false
        })
      )
      .subscribe(
        (application) => this.setApplication(application),
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )

    // Finally, subscribe to this observable again, using the launchSyncSubscription variable.
    // We want to know when the launch completes, not when the actual sync completes, so we just
    // want this subscription to last for 1 emission.
    this.launchSyncSubscription = updateObservable.pipe(first()).subscribe(
      (_) => {
        this.ref.markForCheck()
        this.concludeTestFailoverDialog.close()
      },
      handleArpioError(window.location.href, this.apiService, this.uiService)
    )
  }

  dismissConcludeTestModal() {
    this.concludeTestFailoverDialog.close()
  }

  showSandboxEventsDialog() {
    this.sandboxEventsDialogRef = this.dialogService.open(AwsEventLogsComponent, {
      data: {
        accountId: this.accountService.accountId,
        applicationId: this.applicationId,
      },
      header: 'Network Sandbox Event Log',
      closable: true,
      modal: true,
    })
  }

  get latestSuccessfulTestTimeAgoString() {
    const timestamp = this.application.latestSuccessfulFailoverTestResultTimestamp
    if (timestamp) {
      return timeAgoString(timestamp)
    } else {
      return timestamp === null ? 'Never' : 'Querying'
    }
  }

  get isStandby() {
    return this.application.syncPhase === SyncPhaseDto.STANDBY
  }

  get isFailover() {
    return this.application.syncPhase === SyncPhaseDto.FAILOVER
  }

  get currentRecoveryPointTimeString(): string {
    const rp = this.application.currentRecoveryPoint
    return rp ? shortTimeString(rp.timestamp) : ''
  }

  constantTrackBy(index: number, item: any) {
    return 1
  }

  get showSyncActions() {
    return this.accountService.entitledForCapability('sync')
  }

  get failbackEnabled() {
    return this.accountService.entitledForCapability('failback')
  }

  get resourcesAreQuarantined(): boolean {
    return this.appResourcesLoaded && this.accountService.quarantineEnabled && this.getQuarantinedResources().length > 0
  }

  dismissFailbackModal() {
    this.failbackDialog.close()
  }

  dismissUnquarantineModal() {
    this.unquarantineDialog.close()
  }

  showRecoveryTab() {
    this.currentTab = ResourcesTab.RECOVERY
  }

  showFailbackTab() {
    this.currentTab = ResourcesTab.FAILBACK
  }

  get activeTab(): ResourcesTab {
    return this.currentTab
  }

  get showFailback() {
    return this.failbackEnabled && this.isFailover && !this.resourcesAreQuarantined
  }

  get hasFailbackResources(): boolean {
    return this.appResourcesLoaded && this.getFailbackResources().length > 0
  }

  doFilterResources(filterText: string) {
    this.filterText = filterText
  }

  get applicationSyncPhase() {
    return this.application.syncPhase
  }

  get showNetworkSandboxStatus() {
    return (
      this.accountService.entitledForCapability('networkSandbox') &&
      this.application.syncPhase == SyncPhaseDto.FAILOVER_TEST
    )
  }

  get showSandboxSettingsButton(): boolean {
    return (
      this.application.syncPhase == SyncPhaseDto.FAILOVER_TEST &&
      this.application.networkSandboxEnabled &&
      !this.appIsDeleted &&
      this.canTest
    )
  }

  get showRansomwareRecoveryStatus() {
    return this.accountService.quarantineEnabled && this.application.syncPhase === SyncPhaseDto.FAILOVER
  }

  get networkSandboxStatus() {
    return this.application.networkSandboxEnabled ? 'enabled' : 'disabled'
  }

  get domainAllowlistStatus(): string {
    if (this.application.networkSandboxEnabled && this.application.networkSandboxAllowedDomains) {
      return `Outbound access domains: ${this.application.networkSandboxAllowedDomains.join(', ')}`
    } else {
      return ''
    }
  }

  get whitelistStatus(): string {
    if (this.application.networkSandboxEnabled && this.application.networkSandboxWhitelist) {
      return `Outbound access CIDRs: ${this.application.networkSandboxWhitelist.join(', ')}`
    } else {
      return ''
    }
  }

  get ransomwareRecoveryStatus() {
    return this.application.ransomwareRecoveryEnabled ? 'enabled' : 'disabled'
  }

  get lastFailback(): ProcessingStatus {
    return new ProcessingStatus('failback', this.application.lastFailback)
  }

  get lastSyncComplete(): Date {
    return this.application.lastRestoreComplete
  }

  get recoveryPointAttemptsLoaded(): boolean {
    return !!this.application
  }

  get appResourcesLoaded(): boolean {
    return this.application.isLoaded
  }

  get currentlyRestoring(): boolean {
    return this.application.currentlyRestoring
  }

  /**
   * Determines whether to show the app actions based on various conditions.
   * It checks if the user has the 'appGroupRestore' capability.
   * If so, it further checks if the application is not currently restoring, not currently failing back, and is in standby.
   * Otherwise, it checks if the application is not currently restoring and not currently failing back.
   * Returns true to show app actions and buttons, otherwise false.
   */
  get showAppActions(): boolean {
    if (this.accountService.entitledForCapability('appGroupRestore')) {
      return (
        (!this.application.currentlyRestoring || this.application.syncPhase === SyncPhaseDto.STANDBY) &&
        !this.application.currentlyFailingBack
      )
    }
    return !this.application.currentlyRestoring && !this.application.currentlyFailingBack
  }

  get currentlyFailingBack() {
    return this.application.currentlyFailingBack
  }

  get currentlyActive(): boolean {
    return this.application.currentlyActive
  }

  get nothingActive(): boolean {
    return !(
      subscriptionActive(this.applicationSubscription) || subscriptionActive(this.applicationRefreshTimerSubscription)
    )
  }

  get allActive(): boolean {
    return (
      subscriptionActive(this.applicationSubscription) && subscriptionActive(this.applicationRefreshTimerSubscription)
    )
  }

  private noSyncPairAppsInSyncPhase(syncPhase: SyncPhaseDto) {
    return this.applicationProviderService
      .loadedApplications()
      .filter((app) => Application.sameSyncPair(this.application, app))
      .reduce((accum, app) => accum && app.syncPhase !== syncPhase, true)
  }

  get canTest(): boolean {
    return this.canTestPermission
  }

  get canRecover(): boolean {
    return this.canRecoverPermission
  }

  get canManage(): boolean {
    return this.canManagePermission
  }

  get rbacEnabled(): boolean {
    return this.accountService.rbacEnabled
  }

  // This gets the deleted property on the curren applicationDto
  get appIsDeleted(): boolean {
    return this.application?.dto?.deleted
  }

  get allResourcesCleaned(): boolean {
    return this.application?.resources?.length === 0
  }

  /**
   * Checks if a manual standby restore is in progress to disable actions
   * like launching tests or recovery, preventing conflicts.
   * Returns true if:
   * - A restore is active (`application.currentlyRestoring`).
   * - The sync phase is `STANDBY` (`application.syncPhase`).
   * - The restore was triggered manually (`application.restoreTrigger === RestoreTrigger.MANUAL`).
   */
  get isStandbyRestoreConcluding(): boolean {
    return (
      this.application?.currentlyRestoring &&
      this.application?.syncPhase === SyncPhaseDto.STANDBY &&
      this.application?.restoreTrigger === RestoreTriggerDto.MANUAL
    )
  }

  /**
   * Determines if an application's deletion process has been completed.
   * An application is considered fully deleted when a successful backup occurred
   * after the deletion timestamp AND a successful restore occurred after that backup.
   * This matches the logic used in ApplicationService.setIssues().
   */
  private hasCompletedDeletionProcess(application: any): boolean {
    if (!application?.dto?.deletedAt) {
      return false
    }

    const deletedAt = application.dto.deletedAt
    const lastBackupSuccess = application.dto.backupStatus?.lastSuccess
    const lastRestoreSuccess = application.dto.restoreStatus?.lastSuccess

    // Check if a successful backup occurred after deletion AND a successful restore occurred after that backup
    const hasSyncedAfterDelete = lastBackupSuccess > deletedAt && lastRestoreSuccess > lastBackupSuccess

    return hasSyncedAfterDelete
  }

  get sourceDescription(): string {
    return this.formatEndpointDescription(this.application?.sourceEndpointUri, this.application?.sourceSubscriptionName)
  }

  get targetDescription(): string {
    return this.formatEndpointDescription(this.application?.targetEndpointUri, this.application?.targetSubscriptionName)
  }

  private formatEndpointDescription(endpointUri: string, subscriptionName: string | null): string {
    const [, accountId, region] = endpointUri.split(':')
    return subscriptionName ? `${subscriptionName} (${accountId}) / ${region}` : `${accountId} / ${region}`
  }
}
